import { useEffect, useState } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Button,
    Typography,
    Tooltip,
} from "@material-tailwind/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import ContentWrapper from "components/ui/content-wrapper";
import useSWR from "swr";
import { fetcherSimple } from "utils/api";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { userSelector } from "store/user";
import { SUBSCRIPTION_NAMES } from "utils/consts";

export default function TopUps() {
    const [renewDate, setRenewDate] = useState("");
    const [renewWhen, setRenewWhen] = useState("");
    const [lastPurchase, setLastPurchase] = useState(null);
    const [period, setPeriod] = useState("month");
    const navigate = useNavigate();
    const user = useSelector(userSelector);
    dayjs.extend(relativeTime);

    const { data: creditsData } = useSWR(
        [`/v2/organisation/credits`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
        {
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
        }
    );

    const { data: purchasesData } = useSWR(
        [`/v2/organisation/purchases`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
        {
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
        }
    );


    const { data: productsData } = useSWR(
        [`/v2/stripe_item?stripe_only=1`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const oneTimeProducts = productsData?.filter(product => product.type === 'one_time');


    useEffect(() => {
        if (purchasesData?.length > 0) {
            const recurring_purchase = purchasesData.find(
                (product) => product?.expires,
            );
            if (recurring_purchase) {
                const expiry_date = recurring_purchase?.expires;
                if (expiry_date && dayjs(expiry_date).isValid()) {
                    const renewDateDayjs = dayjs(expiry_date);
                    const renewDateString =
                        dayjs(renewDateDayjs).format("DD/MM/YYYY");
                    const renewWhenString = dayjs().to(renewDateDayjs, true);
                    setRenewDate(renewDateString);
                    setRenewWhen(renewWhenString);
                }
                setPeriod(recurring_purchase?.renews_after || 'year');
            }

            const oneTimePurchases = purchasesData?.filter(p => !p.expires);
            if (oneTimePurchases?.length > 0) {
                const latest = oneTimePurchases.sort((a, b) => dayjs(b.date_purchased).diff(dayjs(a.date_purchased)))[0];
                const datePurchased = dayjs(latest.date_purchased);
                setLastPurchase({
                    date: datePurchased.format("DD/MM/YYYY"),
                    quantity: latest.credits_purchased || 0,
                    timeAgo: datePurchased.isValid() ? datePurchased.fromNow() : ''
                });
            }
        }
    }, [purchasesData]);

    return (
        <>
            <ContentWrapper>
                <div className="mt-4 mx-auto w-fit">
                    <Card
                        className="card mx-2 mb-4 rounded-2xl p-4"
                        shadow={false}
                    >
                        <div className="card-inner">
                            <CardHeader
                                className="card-inner-header w-full m-0 px-4 py-2 text-2xl font-display"
                                color="transparent"
                                floated={true}
                                shadow={false}
                            >
                                Response Credit Top-ups
                            </CardHeader>
                            <Typography variant="paragraph" className="px-4 py-2" color="black">
                                {user.subscriptionDetails}
                            </Typography>
                            <CardBody className=" card-inner-body flex flex-col p-4 rounded-2xl">
                                <div className="w-full p-2 my-2">
                                    <Tooltip content="Your response credit allowance is reset to the full amount for your subscription at each subscription renewal">
                                        <Typography variant="h4">
                                            Subscription Allowance
                                        </Typography>
                                    </Tooltip>
                                    <Typography
                                        variant="paragraph"
                                        className="mb-4"
                                    >
                                        {(user.activeSubscription || user.isManaged) ? (
                                            <>
                                                {period.charAt(0).toUpperCase() + period.slice(1)}ly response credit allowance: {creditsData?.recurring_credits_purchased} responses
                                                <br />
                                                Response credits remaining this {period}: {creditsData?.recurring_credits_remaining} responses
                                                <br />
                                                {!!(renewDate && renewWhen) && (
                                                    <>
                                                        Your allowance will renew on {renewDate} (in {renewWhen}).
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            `${SUBSCRIPTION_NAMES.FREE_INDIVIDUAL} does not receive a response credit allowance`
                                        )}
                                    </Typography>
                                    <Tooltip content="Purchased response credits are not tied to a subscription so unused credits will not expire.">
                                        <Typography variant="h4">
                                            Purchased top-up response credits
                                        </Typography>
                                    </Tooltip>
                                    <Typography variant="paragraph">
                                        You currently have {creditsData?.one_time_credits_remaining} Top-up response credits available
                                        <br />
                                        {lastPurchase ?
                                            `You last purchased ${lastPurchase.quantity ?? 0} credits on ${lastPurchase.date}${lastPurchase.timeAgo ? ` (${lastPurchase.timeAgo})` : ''}`
                                            : 'You have not purchased any top-up credits yet'
                                        }
                                        <br />
                                    </Typography>
                                </div>
                                <Typography variant="h4" className="text-center mb-4">Need a top-up?</Typography>
                                <div className="w-full rounded-2xl  grid grid-cols-1 md:grid-cols-2 gap-2 p-2">
                                    {!!oneTimeProducts &&
                                        oneTimeProducts?.map((product) => (
                                            <div
                                                className="text-center"
                                                key={product.sku}
                                            >
                                                <Button
                                                    className="btn btn--cta w-full"
                                                    onClick={() =>
                                                        navigate(
                                                            `/payment?product_sku=${product.sku}`,
                                                        )
                                                    }
                                                >
                                                    {product.description} {product.price > 1
                                                        ? Intl.NumberFormat(
                                                            "en-EN",
                                                            {
                                                                style: "currency",
                                                                currency:
                                                                        "GBP",
                                                                trailingZeroDisplay:
                                                                        "stripIfInteger",
                                                            },
                                                        ).format(
                                                            product.price /
                                                                100,
                                                        )
                                                        : "Free"}
                                                </Button>
                                            </div>
                                        ))}
                                </div>
                            </CardBody>
                        </div>
                    </Card>
                </div>
                <div className="basis-1/3 rounded-xl min-h-[100px] mt-4"></div>
            </ContentWrapper>
        </>
    );
}
